import React, { useEffect, useRef } from 'react';
import { beginOperation, endOperation } from '../services/idempotency';

const FOCUSABLE = 'input, select, textarea, button, [href], [tabindex]:not([tabindex="-1"])';

const Modal = ({ isOpen, onClose, title, children, size = 'lg' }) => {
  const dialogRef = useRef(null);
  const previouslyFocused = useRef(null);
  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }
    beginOperation();
    return endOperation;
  }, [isOpen]);

  useEffect(() => {
    const handleEscapeKey = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscapeKey);
      return () => {
        document.removeEventListener('keydown', handleEscapeKey);
      };
    }
  }, [isOpen, onClose]);

  /* Move the caret into the dialog on open and hand it back on close, so a
     keyboard user is not left tabbing through the page behind the overlay. */
  useEffect(() => {
    if (!isOpen) return undefined;

    previouslyFocused.current = document.activeElement;

    const body = dialogRef.current?.querySelector('.modal-body');
    const firstField = body?.querySelector(FOCUSABLE) || dialogRef.current?.querySelector(FOCUSABLE);
    firstField?.focus?.();

    return () => previouslyFocused.current?.focus?.();
  }, [isOpen]);

  const handleTabKey = (event) => {
    if (event.key !== 'Tab') return;

    const focusable = [...(dialogRef.current?.querySelectorAll(FOCUSABLE) || [])]
      .filter((element) => !element.disabled && element.offsetParent !== null);
    if (focusable.length === 0) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const wrappingBackwards = event.shiftKey && document.activeElement === first;
    const wrappingForwards = !event.shiftKey && document.activeElement === last;

    if (wrappingBackwards || wrappingForwards) {
      event.preventDefault();
      (event.shiftKey ? last : first).focus();
    }
  };

  if (!isOpen) return null;

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div
      className="modal fade show d-block"
      style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)', zIndex: 1050 }}
      onClick={handleOverlayClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="modalTitle"
    >
      <div
        className={`modal-dialog modal-dialog-centered modal-${size} modal-dialog-scrollable`}
        ref={dialogRef}
        onKeyDown={handleTabKey}
      >
        <div className="modal-content">
          <div className="modal-header border-bottom">
            <h5 className="modal-title" id="modalTitle">{title}</h5>
            <button
              type="button"
              className="btn-close"
              aria-label="Close"
              onClick={onClose}
              style={{
                background: 'none',
                border: 'none',
                fontSize: '1.5rem',
                cursor: 'pointer',
                padding: '0.25rem',
                minWidth: '44px',
                minHeight: '44px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <i className="fas fa-times"></i>
            </button>
          </div>
          <div className="modal-body">
            {children}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Modal;